import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PushNotificationSettings } from '@/components/PushNotificationSettings';
import { supabase } from '@/integrations/supabase/client';
import { Bell, Smartphone } from 'lucide-react';

interface SendLogRow {
  id: string;
  title: string | null;
  platform: string | null;
  status: string;
  status_code: number | null;
  error: string | null;
  created_at: string;
}

export const PushNotificationManagement = () => {
  const [platformCounts, setPlatformCounts] = useState<Record<string, number>>({});
  const [logs, setLogs] = useState<SendLogRow[]>([]);

  useEffect(() => {
    const load = async () => {
      const { data: subs } = await supabase.from('push_subscriptions').select('platform');
      const counts: Record<string, number> = {};
      (subs ?? []).forEach((s: any) => {
        const key = s.platform || 'unknown';
        counts[key] = (counts[key] ?? 0) + 1;
      });
      setPlatformCounts(counts);

      const { data: logRows } = await supabase
        .from('push_send_log')
        .select('id, title, platform, status, status_code, error, created_at')
        .order('created_at', { ascending: false })
        .limit(25);
      setLogs((logRows as SendLogRow[]) ?? []);
    };
    load();
  }, []);

  const totalDevices = Object.values(platformCounts).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-2 sm:items-center">
        <Bell className="h-5 w-5" />
        <h3 className="min-w-0 break-words text-lg font-semibold">Push ծանուցումների կառավարում</h3>
      </div>

      <div className="grid gap-6 md:grid-cols-1">
        <PushNotificationSettings />

        <Card className="border-border/70 bg-card/80">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Ակտիվ սարքեր ({totalDevices})
            </CardTitle>
            <CardDescription>Բաժանորդագրված սարքերը ըստ պլատֆորմի</CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            {totalDevices === 0 ? (
              <p className="text-sm text-muted-foreground">Դեռ բաժանորդագրված սարքեր չկան</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {Object.entries(platformCounts).map(([platform, count]) => (
                  <Badge key={platform} variant="outline">
                    {platform}: {count}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/70 bg-card/80">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle>Վերջին ուղարկումները</CardTitle>
            <CardDescription>Վերջին 25 ուղարկման արդյունքները</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 px-4 sm:px-6">
            {logs.length === 0 ? (
              <p className="text-sm text-muted-foreground">Դեռ ուղարկումներ չկան</p>
            ) : (
              logs.map((log) => (
                <div
                  key={log.id}
                  className="flex flex-col gap-2 border-b pb-3 text-sm last:border-0 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="break-words font-medium">{log.title || '—'}</p>
                    <p className="break-all text-xs text-muted-foreground sm:break-words">
                      {new Date(log.created_at).toLocaleString('hy-AM')} · {log.platform || 'unknown'}
                      {log.error ? ` · ${log.error.slice(0, 80)}` : ''}
                    </p>
                  </div>
                  <Badge className="w-fit shrink-0"
                    variant={
                      log.status === 'sent'
                        ? 'default'
                        : log.status === 'expired'
                          ? 'secondary'
                          : 'destructive'
                    }
                  >
                    {log.status}
                    {log.status_code ? ` (${log.status_code})` : ''}
                  </Badge>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card className="border-border/70 bg-card/80">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle>Ծանուցումների տեսակները</CardTitle>
            <CardDescription>PWA-ն աջակցում է հետևյալ ծանուցումների տեսակները</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 px-4 sm:px-6">
            <div className="grid gap-4">
              <div className="flex items-start gap-3">
                <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary"></div>
                <div>
                  <p className="font-medium">In-App ծանուցումներ</p>
                  <p className="text-sm text-muted-foreground">
                    Տեսանելի են միայն այցելելիս էջը - բոլոր օգտատերերի համար ակտիվ
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary"></div>
                <div>
                  <p className="font-medium">Native Push ծանուցումներ</p>
                  <p className="text-sm text-muted-foreground">
                    Տեսանելի են նույնիսկ երբ էջը փակ է - պահանջում է օգտատիրոջ թույլտվություն
                    (iOS 16.4+ դեպքում՝ Home Screen-ից բացված հավելված)
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary"></div>
                <div>
                  <p className="font-medium">PWA Badge ծանուցումներ</p>
                  <p className="text-sm text-muted-foreground">
                    Ցուցադրում է չկարդացած ծանուցումների քանակը app icon-ի վրա
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
